/**
 * Write New Post Form Component
 * Form for creating a new post, optionally in response to a prompt.
 * Supports three word categories: 100, 250, and 500 words.
 */

import React, { useRef, useEffect } from 'react';
import { capture } from '../../utils/analytics';

// Autosave drafts so a refresh or accidental navigation doesn't lose writing.
const draftKeyFor = (prompt) =>
  prompt ? `writeflow:draft:prompt:${prompt._id}` : 'writeflow:draft:general';

const MIN_TEXTAREA_HEIGHT = 100;
const MAX_TEXTAREA_HEIGHT = 400;

// Word category limits (enforced by word count only)
const CATEGORY_WORD_LIMITS = { 100: 100, 250: 250, 500: 500 };

const WriteNewPostForm = ({
  user,
  prompt,
  postContent,
  onContentChange,
  onSubmit,
  onCancel,
  selectedCategory = 250,
  onCategoryChange
}) => {
  const textareaRef = useRef(null);
  const selectionRef = useRef(null);
  const draftRestoredRef = useRef(false);
  // prompt_viewed -> write_started -> post_created micro-funnel: fire once per
  // composer open, on first focus of the textarea.
  const writeStartedRef = useRef(false);
  const draftKey = draftKeyFor(prompt);
  const wordCount = postContent.trim() ? postContent.trim().split(/\s+/).length : 0;
  const wordLimit = CATEGORY_WORD_LIMITS[selectedCategory];

  // Restore a saved draft once on mount (only if the field is empty).
  useEffect(() => {
    if (draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    if (!postContent) {
      const saved = localStorage.getItem(draftKey);
      if (saved) onContentChange(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  // Persist the draft as the user types; clear it once the field is empty (e.g. after posting).
  useEffect(() => {
    if (postContent && postContent.trim()) {
      localStorage.setItem(draftKey, postContent);
    } else {
      localStorage.removeItem(draftKey);
    }
  }, [postContent, draftKey]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;

    const savedScrollY = window.scrollY;
    const savedScrollX = window.scrollX;

    const style = getComputedStyle(ta);
    const lineHeight = parseFloat(style.lineHeight) || 24;

    ta.style.height = `${MIN_TEXTAREA_HEIGHT}px`;
    const scrollHeight = ta.scrollHeight;
    const clientHeight = ta.clientHeight;

    if (scrollHeight >= clientHeight - lineHeight) {
      const newHeight = Math.min(MAX_TEXTAREA_HEIGHT, scrollHeight + lineHeight);
      ta.style.height = `${newHeight}px`;
    }

    // Always restore scroll so the browser never scrolls the page to focus the textarea
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(savedScrollX, savedScrollY);
      });
    });

    // Restore selection if we have it (this fixes the cursor jump bug when auto-capitalizing)
    if (selectionRef.current && ta) {
      const { start, end } = selectionRef.current;
      ta.setSelectionRange(start, end);
      selectionRef.current = null;
    }
  }, [postContent]);

  const isOverWordLimit = wordCount > wordLimit;
  const isNearLimit = wordCount >= wordLimit * 0.9;

  return (
    <div className="create-post-card">
      <form onSubmit={onSubmit}>
        <div className="create-post-header">
          {user?.avatar ? (
            <img src={user.avatar} alt={user.username} className="post-avatar" />
          ) : (
            <div className="post-avatar-placeholder">
              {user?.username?.charAt(0).toUpperCase()}
            </div>
          )}
          <span>{user?.fullName || user?.username}</span>
        </div>
        
        {/* Word Category Selector */}
        <div className="word-category-selector">
          <span className="category-label">Word limit:</span>
          <div className="category-buttons">
            {[100, 250, 500].map((category) => (
              <button
                key={category}
                type="button"
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => onCategoryChange?.(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <textarea
          ref={textareaRef}
          value={postContent}
          onFocus={() => {
            if (writeStartedRef.current) return;
            writeStartedRef.current = true;
            capture('write_started', { hasPrompt: !!prompt });
          }}
          onChange={(e) => {
            const { selectionStart, selectionEnd } = e.target;
            selectionRef.current = { start: selectionStart, end: selectionEnd };

            let value = e.target.value;
            value = value.replace(/\.\s*([a-z])/g, (match) => match.slice(0, -1) + match.slice(-1).toUpperCase());
            onContentChange(value);

            // ponytail: only scale up — user controls scale-down via buttons
            const wc = value.trim() ? value.trim().split(/\s+/).length : 0;
            if (wc > 250 && selectedCategory < 500) onCategoryChange?.(500);
            else if (wc > 100 && selectedCategory < 250) onCategoryChange?.(250);
          }}
          placeholder={prompt ? "Write your creative response to today's prompt..." : "What's on your mind?"}
          rows={6}
        />
        <p className="format-hint">
          Formatting: <strong>**bold**</strong>, <em>*italic*</em>, <code>`code`</code> · #tags and @mentions link automatically
        </p>
        <div className="create-post-footer">
          <div className="create-post-counts">
            <span className={`word-count ${isOverWordLimit ? 'over-limit' : isNearLimit ? 'near-limit' : ''}`}>
              {wordCount}/{wordLimit} words
            </span>
            {wordCount > 0 && <span className="draft-saved">· Draft saved</span>}
          </div>
          <button 
            type="submit" 
            className="btn-post"
            disabled={isOverWordLimit || wordCount === 0}
          >
            {prompt ? 'Share Response' : 'Post'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WriteNewPostForm;
